import { createApi } from '@reduxjs/toolkit/query/react';
import { newBackendAxiosBaseQuery } from '../../utils/axiosBaseQuery';
import { serializeQueryArgsWithTenant } from '../tenantCacheKey';

// Utilization for one group — GET /admin/group/:groupId/occupancy on the new
// (ECS) backend.
//
// New-backend base query on purpose: this endpoint is derived from the booking
// guard (`assertCapacityAvailableTx`), not from either availability endpoint.
// Those are what this view exists to check, so deriving it from them would
// validate them against themselves. See
// docs/reviews/availability-computation-divergence.md.

/** One display slot. The slot runs from `from` to the next cell's `from`. */
export interface OccupancyCell {
    from: string;
    /** Units taken at the busiest moment inside the slot. */
    used: number;
    /** capacity − used, floored at 0. */
    free: number;
    /**
     * Is any part of what this cell shows NOT firm? (#2634 item 3)
     *
     * True when a hold or an unsettled booking touches the slot — a customer
     * in checkout, or a postpay offer nobody has accepted yet. Drawn hatched
     * rather than solid so a week that looks full can still be read as partly
     * unsold.
     *
     * Optional because a cached admin bundle can outlive a deploy: an older
     * response simply draws every block solid, as it always did.
     */
    hasReserved?: boolean;
}

export interface OccupancyDay {
    /** `YYYY-MM-DD` in the group location's zone (the response's `timeZone`). */
    date: string;
    cells: OccupancyCell[];
    /** Busiest moment of the day. */
    peakUsed: number;
}

export interface GroupOccupancyResponse {
    groupId: string;
    groupName: string | null;
    /** Bookable assets in the group — the ceiling `used` is measured against. */
    capacity: number;
    slotMinutes: number;
    from: string;
    to: string;
    days: OccupancyDay[];
    /**
     * #3219: the IANA zone of the group's location — the zone `days` are keyed
     * in. Absent on an older server, which keyed them in the station default.
     */
    timeZone?: string;
    /**
     * ADR-0027 (M4/M12): the allocation windows binding this room in the
     * range, released dates left out. Rooms only; absent on an older server.
     */
    windows?: OccupancyWindow[];
}

/** One occurrence of an organisation's allocation window. */
export interface OccupancyWindow {
    windowId: string;
    /** The room the window is on: this one, or the hall / part it is divided with. */
    groupId: string;
    organizationId: string;
    organizationName: string | null;
    /** Local date of the occurrence, `YYYY-MM-DD`. */
    date: string;
    /** ISO instants. */
    start: string;
    end: string;
    /**
     * D7: the window pauses on this holiday, so the time is free for anyone.
     * Absent (older server) or null = the window holds.
     */
    paused?: 'public' | 'school' | null;
}

export interface GetGroupOccupancyArgs {
    groupId: string;
    /** ISO date-times. The backend rejects ranges longer than 31 days. */
    from: string;
    to: string;
    /** Display bucket size; the backend default is 15. */
    slotMinutes?: number;
}

export const occupancyApiSlice = createApi({
    reducerPath: 'occupancyApiSlice',
    serializeQueryArgs: serializeQueryArgsWithTenant,
    baseQuery: newBackendAxiosBaseQuery(),
    tagTypes: ['GroupOccupancy'],
    endpoints: (builder) => ({
        getGroupOccupancy: builder.query<GroupOccupancyResponse, GetGroupOccupancyArgs>({
            query: ({ groupId, from, to, slotMinutes }) => {
                const params = new URLSearchParams({ from, to });
                if (slotMinutes) params.set('slotMinutes', String(slotMinutes));
                return {
                    url: `/group/${groupId}/occupancy?${params.toString()}`,
                    method: 'GET',
                };
            },
            providesTags: (result, error, { groupId }) => [
                { type: 'GroupOccupancy', id: groupId },
            ],
        }),
    }),
});

export const { useGetGroupOccupancyQuery } = occupancyApiSlice;
